/**
 * Category Service
 * Handles CRUD operations for tour categories
 * Supports both localStorage (main branch) and backend API (this branch)
 */

import { USE_BACKEND_API } from '../config/apiConfig';
import { backendCategoryService } from './backendCategoryService';

export interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  isActive: boolean;
  active?: boolean; // Backend uses 'active', frontend uses 'isActive'
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

class CategoryService {
  private readonly STORAGE_KEY = 'tourCategories';
  private readonly DEFAULT_CATEGORIES: Category[] = [];

  /**
   * Get all categories
   */
  async getAllCategories(forceRefresh: boolean = false): Promise<Category[]> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        const backendCategories = await backendCategoryService.getAllCategories(false, forceRefresh);
        if (backendCategories && backendCategories.length > 0) {
          // Map backend format to frontend format
          return backendCategories.map(cat => ({
            ...cat,
            isActive: cat.active !== false,
          }));
        }
        console.warn('⚠️ Backend returned 0 categories. Returning empty list to prevent ghost data.');
        return [];
      } catch (error) {
        console.error('❌ Error getting categories from backend:', error);
        return [];
      }
    }

    // Use localStorage (main branch behavior)
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const categories = JSON.parse(stored);
        return categories.sort((a: Category, b: Category) => a.name.localeCompare(b.name));
      }

      // Initialize with default categories if none exist
      await this.initializeDefaultCategories();
      return this.DEFAULT_CATEGORIES;
    } catch (error) {
      console.error('Error getting categories:', error);
      return this.DEFAULT_CATEGORIES;
    }
  }

  /**
   * Get active categories only
   */
  async getActiveCategories(): Promise<Category[]> {
    const allCategories = await this.getAllCategories();
    return allCategories.filter(category => category.isActive);
  }

  /**
   * Get category by ID
   */
  async getCategoryById(id: string): Promise<Category | null> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        const category = await backendCategoryService.getCategoryById(id);
        if (category) {
          return {
            ...category,
            isActive: category.active !== false,
          };
        }
        return null;
      } catch (error) {
        console.error('Error getting category from backend:', error);
        // Fallback to localStorage
      }
    }

    // Use localStorage (main branch behavior)
    const categories = await this.getAllCategories();
    return categories.find(category => category.id === id) || null;
  }

  /**
   * Create a new category
   */
  async createCategory(categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<Category> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        // Map frontend format to backend format
        const backendData = {
          name: categoryData.name,
          description: categoryData.description,
          color: categoryData.color,
          icon: categoryData.icon,
          active: categoryData.isActive !== false,
        };

        const backendCategory = await backendCategoryService.createCategory(backendData);

        // Map backend format to frontend format
        return {
          ...backendCategory,
          isActive: backendCategory.active !== false,
        };
      } catch (error) {
        console.error('Error creating category in backend:', error);
        throw error;
      }
    }

    // Use localStorage (main branch behavior)
    const categories = await this.getAllCategories();

    // Check if category name already exists
    const existingCategory = categories.find(cat =>
      cat.name.toLowerCase() === categoryData.name.toLowerCase()
    );

    if (existingCategory) {
      throw new Error('Category with this name already exists');
    }

    const newCategory: Category = {
      ...categoryData,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    categories.push(newCategory);
    await this.saveCategories(categories);

    return newCategory;
  }

  /**
   * Update an existing category
   */
  async updateCategory(id: string, updates: Partial<Omit<Category, 'id' | 'createdAt'>>): Promise<Category> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        // Map frontend format to backend format
        const backendUpdates: any = {};
        if (updates.name !== undefined) backendUpdates.name = updates.name;
        if (updates.description !== undefined) backendUpdates.description = updates.description;
        if (updates.color !== undefined) backendUpdates.color = updates.color;
        if (updates.icon !== undefined) backendUpdates.icon = updates.icon;
        if (updates.isActive !== undefined) backendUpdates.active = updates.isActive;
        if (updates.sortOrder !== undefined) backendUpdates.sortOrder = updates.sortOrder;

        const backendCategory = await backendCategoryService.updateCategory(id, backendUpdates);

        // Map backend format to frontend format
        return {
          ...backendCategory,
          isActive: backendCategory.active !== false,
        };
      } catch (error) {
        console.error('Error updating category in backend:', error);
        throw error;
      }
    }

    // Use localStorage (main branch behavior)
    const categories = await this.getAllCategories();
    const categoryIndex = categories.findIndex(cat => cat.id === id);

    if (categoryIndex === -1) {
      throw new Error('Category not found');
    }

    // Check if new name conflicts with existing categories
    if (updates.name) {
      const existingCategory = categories.find(cat =>
        cat.id !== id && cat.name.toLowerCase() === updates.name!.toLowerCase()
      );

      if (existingCategory) {
        throw new Error('Category with this name already exists');
      }
    }

    const updatedCategory: Category = {
      ...categories[categoryIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    categories[categoryIndex] = updatedCategory;
    await this.saveCategories(categories);

    return updatedCategory;
  }

  /**
   * Delete a category
   */
  async deleteCategory(id: string): Promise<boolean> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        return await backendCategoryService.deleteCategory(id);
      } catch (error) {
        console.error('Error deleting category in backend:', error);
        throw error;
      }
    }

    // Use localStorage (main branch behavior)
    const categories = await this.getAllCategories();
    const categoryIndex = categories.findIndex(cat => cat.id === id);

    if (categoryIndex === -1) {
      throw new Error('Category not found');
    }

    // Check if category is being used by any tours
    const { tourService } = await import('./tourService');
    const tours = await tourService.getAllTours();
    const toursUsingCategory = tours.filter(tour => tour.category === categories[categoryIndex].name);

    if (toursUsingCategory.length > 0) {
      throw new Error(`Cannot delete category. It is being used by ${toursUsingCategory.length} tour(s).`);
    }

    categories.splice(categoryIndex, 1);
    await this.saveCategories(categories);

    return true;
  }

  /**
   * Toggle category active status
   */
  async toggleCategoryStatus(id: string): Promise<Category> {
    // Use backend API if configured
    if (USE_BACKEND_API) {
      try {
        const category = await this.getCategoryById(id);
        if (!category) {
          throw new Error('Category not found');
        }
        return await this.updateCategory(id, { isActive: !category.isActive });
      } catch (error) {
        console.error('Error toggling category status in backend:', error);
        throw error;
      }
    }

    // Use localStorage (main branch behavior)
    const category = await this.getCategoryById(id);
    if (!category) {
      throw new Error('Category not found');
    }

    return await this.updateCategory(id, { isActive: !category.isActive });
  }

  /**
   * Get category names for dropdowns
   */
  async getCategoryNames(): Promise<string[]> {
    const activeCategories = await this.getActiveCategories();
    return activeCategories.map(category => category.name);
  }

  /**
   * Initialize default categories
   */
  private async initializeDefaultCategories(): Promise<void> {
    await this.saveCategories(this.DEFAULT_CATEGORIES);
  }

  /**
   * Save categories to localStorage
   */
  private async saveCategories(categories: Category[]): Promise<void> {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(categories));
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Clear all categories (for testing)
   */
  async clearAllCategories(): Promise<void> {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Clear category cache
   */
  clearCache(): void {
    if (USE_BACKEND_API) {
      backendCategoryService.clearCache();
    }
  }
}

export const categoryService = new CategoryService();
